import { ApplicationId } from "@1games-ru/region-shared/dist/enums/phone/application-id";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useState } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useGetRefreshableServerDateAndTime from "@/core/helpers/useGetRefreshableServerDateAndTime";
import { usePreloadImages } from "@/core/helpers/usePreloadImages";
import { imgPhoneBgLock, imgPhoneCamera, imgPhonePhone } from "@/shared/assets/img";
import { PhoneSystemAppsListEnum } from "@/domains/phone/constants/phoneSystemAppsListEnum";
import {
  devicesList,
  phoneActiveApp,
  phoneIsShow,
  phoneSystemActiveApp,
  setActiveApp,
  setSystemActiveApp,
} from "@/domains/phone/slice";
import useShowPhone from "@/domains/phone/hooks/useShowPhone";
import PhoneBody from "@/domains/phone/components/PhoneBody";
import PhoneHomeScreen from "@/domains/phone/components/PhoneHomeScreen";
import PhoneLockScreen from "@/domains/phone/components/PhoneLockScreen";
import PhoneWrapper from "@/domains/phone/components/PhoneWrapper";

function FakeApp() {
  return <div className="flex justify-center items-center size-full bg-dark-800">FakeApp</div>;
}

const imgList = [imgPhonePhone, imgPhoneBgLock, "/img/phone/bg-screen.webp", imgPhoneCamera];

export default function PhoneContainer() {
  const dispatch = useAppDispatch();
  const activeApp = useAppSelector(phoneActiveApp);
  const systemActiveApp = useAppSelector(phoneSystemActiveApp);
  const [isLock, setIsLock] = useState(true);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const isOpen = useAppSelector(phoneIsShow);
  const appsList = useAppSelector(devicesList);

  const activeAppId = activeApp ?? systemActiveApp;
  const currentTime = useGetRefreshableServerDateAndTime(isOpen, activeAppId as number);

  const changeActiveApp = useCallback(
    (appId: ApplicationId) => dispatch(setActiveApp(appId)),
    [dispatch],
  );

  const changeSystemActiveApp = useCallback(
    (appId: PhoneSystemAppsListEnum) => dispatch(setSystemActiveApp(appId)),
    [dispatch],
  );

  const appContent = {
    [ApplicationId.Taxi]: FakeApp,
    [ApplicationId.RentScooter]: FakeApp,
    [ApplicationId.CellularCommunication]: FakeApp,
  };

  const systemAppContent = {
    [PhoneSystemAppsListEnum.Calls]: FakeApp,
    [PhoneSystemAppsListEnum.Messages]: FakeApp,
    [PhoneSystemAppsListEnum.Settings]: FakeApp,
    [PhoneSystemAppsListEnum.Contacts]: FakeApp,
  };

  const handleIconClick = (event) => {
    const iconRect = event.currentTarget.getBoundingClientRect();
    const containerRect = event.currentTarget.parentElement.getBoundingClientRect();
    const x = iconRect.left - containerRect.left;
    const y = iconRect.top - containerRect.top;
    setOrigin({ x, y });
  };

  useShowPhone(setIsLock);

  const unlockPhone = useCallback(() => setIsLock(false), []);

  usePreloadImages(imgList);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-y-[--boundary-offset-y] inset-x-[--boundary-offset-x] flex flex-col justify-center items-end z-40"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{
            y: "100%",
            transition: {
              delay: 0.2,
              stiffness: 400,
            },
          }}
          transition={{ stiffness: 400 }}
        >
          <PhoneWrapper>
            <PhoneBody>
              <motion.div
                className="absolute z-20 top-0 left-0 size-full pointer-events-none"
                exit={{ backgroundColor: "#121212" }}
              />
              <PhoneLockScreen isLock={isLock} currentTime={currentTime} onClick={unlockPhone} />
              <AnimatePresence>
                {typeof activeAppId === "number" && (
                  <motion.div
                    className="absolute z-1 top-0 left-0 size-full"
                    initial={{ scale: 0.1, opacity: 0, x: origin.x, y: origin.y }}
                    animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
                    exit={{ scale: 0.1, opacity: 0, x: origin.x, y: origin.y }}
                    transition={{ type: "spring", damping: 20, stiffness: 200 }}
                    style={{ originX: 0, originY: 0 }}
                  >
                    {typeof activeApp === "number"
                      ? React.createElement(appContent[activeApp])
                      : React.createElement(systemAppContent[systemActiveApp])}
                  </motion.div>
                )}
              </AnimatePresence>
              <PhoneHomeScreen
                appsList={appsList}
                currentTime={currentTime}
                changeActiveApp={changeActiveApp}
                changeSystemActiveApp={changeSystemActiveApp}
                handleIconClick={handleIconClick}
              />
            </PhoneBody>
          </PhoneWrapper>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
